import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import useIsMount from '../../hooks/use-mount';

const numbersOnlyRegex = /[^0-9]/;
const backspace = 8;

const isNotPositiveWholeNumber = value => {
  return numbersOnlyRegex.test(value);
};

export default function NumberBox(props) {
  const {onChange, onBlur, isInvalid, className, value, disabled, min, max, name, ...rest} = props;
  const [inputValue, setInputValue] = React.useState(value || 0);
  const [maxLength, setMaxLength] = React.useState('');
  const minLimit = min < 0 ? NumberBox.defaultProps.min : min;
  const maxLimit = max < 0 ? NumberBox.defaultProps.max : max;
  const isMount = useIsMount();

  if (min < 0 || max < 0) {
    console.warn(
      `NumberBox: Negative values for 'min' and 'max' are not supported. Defaulting to ${NumberBox.defaultProps.min} and ${NumberBox.defaultProps.max}.`,
    );
  }

  React.useEffect(() => {
    setInputValue(value);
  }, [value]);

  React.useEffect(() => {
    if (!isMount) {
      setInputValue(inputValue);
      onChange(inputValue);
    }
  }, [inputValue]);

  React.useEffect(() => {
    setMaxLength(maxLimit.toString().length);
  }, [maxLimit]);

  const decrementInput = React.useCallback(() => {
    if (disabled) {
      return;
    }
    let val = inputValue;
    if (val <= minLimit) {
      val = minLimit;
    } else {
      val = inputValue - 1;
    }
    setInputValue(val);
  });

  const incrementInput = React.useCallback(() => {
    if (disabled) {
      return;
    }
    let val = inputValue;
    if (val >= maxLimit) {
      val = maxLimit;
    } else {
      val = inputValue + 1;
    }
    setInputValue(val);
  });

  const onBlurHandler = React.useCallback((...params) => {
    if (inputValue < minLimit) {
      setInputValue(minLimit);
    }
    if (inputValue > maxLimit) {
      setInputValue(maxLimit);
    }
    if (onBlur) {
      onBlur(...params);
    }
  });

  const onInputChangeHandler = React.useCallback(changeEvent => {
    const val = changeEvent.target.value ? +changeEvent.target.value : 0;
    setInputValue(val);
  });

  const onKeyPressHandler = React.useCallback(event => {
    const key = String.fromCharCode(event.which);
    if (event.which !== 0 && event.which !== backspace && isNotPositiveWholeNumber(key)) {
      event.preventDefault();
    }
  });

  const onPasteHandler = React.useCallback(event => {
    event.clipboardData.items[0].getAsString(text => {
      const value = isNotPositiveWholeNumber(text) || text.length > maxLength ? 0 : +text;
      setInputValue(value);
    });
  });

  return (
    <div className={classnames('input-group', 'number-box', className)} {...rest} disabled={disabled}>
      <div className="input-group-prepend" onClick={decrementInput}>
        <span className="input-group-text">
          <i className="fa fa-minus" />
        </span>
      </div>
      <input
        className={`form-control ${isInvalid ? 'is-invalid' : ''}`}
        name={name}
        data-format="number"
        min={minLimit}
        max={maxLimit}
        maxLength={maxLength}
        value={inputValue}
        onChange={onInputChangeHandler}
        onKeyPress={onKeyPressHandler}
        onPaste={onPasteHandler}
        disabled={disabled}
        onBlur={onBlurHandler}
      />
      <div className="input-group-append" onClick={incrementInput}>
        <span className="input-group-text">
          <i className="fa fa-plus" />
        </span>
      </div>
    </div>
  );
}

NumberBox.defaultProps = {
  disabled: false,
  min: 0,
  max: 99,
};

NumberBox.propTypes = {
  /** A callback to execute when input changes. Will be passed the input value */
  onChange: PropTypes.func,
  /** A callback to execute when the input loses focus. */
  onBlur: PropTypes.func,
  /** An optional className to add to the HTML element. */
  className: PropTypes.string,
  /** Value of the numberbox */
  value: PropTypes.number,
  /** Name of the numberbox */
  name: PropTypes.string,
  /** Minimum limit of the numberbox */
  min: PropTypes.number,
  /** Maximum limit of the numberbox */
  max: PropTypes.number,
  /** A boolean when passed disables the numberbox */
  disabled: PropTypes.bool,
  /** A boolean indicating that the field is set to an invalid value. */
  isInvalid: PropTypes.bool,
};
